import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Command, ShieldCheck, XCircle } from "lucide-react";

export default function SetupPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("Token invite tidak ditemukan di URL."); return; }
    api.get(`/invites/token/${token}`)
      .then(r => setPreview(r.data))
      .catch(err => setError(err?.response?.data?.detail || "Invite tidak valid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password minimal 6 karakter");
    if (password !== confirm) return toast.error("Password tidak cocok");
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/token/${token}/accept`, { password });
      localStorage.setItem("dmcc_token", data.token);
      setUser(data.user);
      toast.success(`Selamat datang, ${data.user.name}!`);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal set password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white p-6 bg-grid">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#007AFF] flex items-center justify-center"><Command size={20} strokeWidth={2.5} /></div>
          <div>
            <div className="font-display font-black text-lg tracking-tight">DMCC</div>
            <div className="label-mono">accept invitation</div>
          </div>
        </div>

        {error ? (
          <div className="border border-[#FF3B30]/40 border-l-4 border-l-[#FF3B30] bg-[#0A0A0A] p-6" data-testid="invite-error">
            <XCircle size={24} className="text-[#FF3B30] mb-3" />
            <h2 className="font-display font-bold text-xl">Invite Tidak Valid</h2>
            <p className="text-zinc-400 text-sm mt-2">{error}</p>
            <button onClick={() => nav("/login")}
              className="mt-4 label-mono px-4 py-2 border border-[#1F1F1F] hover:border-[#007AFF]">
              → ke halaman login
            </button>
          </div>
        ) : !preview ? (
          <div className="label-mono text-zinc-500"><span className="terminal-cursor">memverifikasi invite</span></div>
        ) : (
          <form onSubmit={submit} className="border border-[#1F1F1F] bg-[#0A0A0A] p-8 space-y-5" data-testid="setup-password-form">
            <div className="flex items-center gap-2 text-[#00E676] label-mono">
              <ShieldCheck size={12} /> invite valid
            </div>
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight">Selamat datang, {preview.name}</h2>
              <p className="text-sm text-zinc-500 mt-2">
                Anda diundang oleh <span className="text-white">{preview.invited_by_name}</span> untuk bergabung sebagai <span className="text-[#00F0FF]">{preview.role_label}</span>.
              </p>
            </div>
            <div className="border border-[#1F1F1F] bg-[#050505] p-3 space-y-1">
              <div className="label-mono text-zinc-600">email</div>
              <div className="font-mono text-sm">{preview.email}</div>
            </div>
            <div>
              <label className="label-mono block mb-2">buat password (min. 6 karakter)</label>
              <input type="password" required minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                autoFocus
                data-testid="setup-password-input"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-3 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            <div>
              <label className="label-mono block mb-2">konfirmasi password</label>
              <input type="password" required minLength={6}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="setup-password-confirm"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-3 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            <button type="submit" disabled={busy}
              data-testid="setup-password-submit"
              className="w-full bg-[#007AFF] hover:bg-[#0056b3] text-white py-3 font-mono text-sm uppercase tracking-[0.2em] transition disabled:opacity-50">
              {busy ? "memproses..." : "→ aktifkan akun"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
